import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Smartphone, Share, Plus, MoreVertical, Download, Apple, QrCode } from "lucide-react";

const isStandalone = () =>
  (typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches) ||
  (typeof navigator !== "undefined" && navigator.standalone === true);
const detectMobile = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /android|iphone|ipad|ipod|mobile/i.test(ua) ||
    (typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches && "ontouchstart" in window);
};

const IT = {
  nl: { qrTitle: "Open op je telefoon", qrText: "Scan de QR-code met je telefoon om deze pagina te openen en op je startscherm te zetten.",
        addBtn: "Voeg toe aan startscherm", dialogTitle: "Toevoegen aan startscherm",
        dialogDesc: "Zet Costa Key als app-icoon op je telefoon. Het icoon opent direct jouw persoonlijke pagina en chat.",
        installNow: "App nu installeren", iosTitle: "iPhone / iPad (Safari)", androidTitle: "Android (Chrome)",
        ios: ["Tik onderin op het deel-icoon", "Kies 'Zet op beginscherm'", "Tik op 'Voeg toe' — klaar!"],
        android: ["Tik rechtsboven op het menu (drie puntjes)", "Kies 'Installeren en snelkoppeling maken'", "Bevestig 'installeren' — het icoon verschijnt op je startscherm"] },
  en: { qrTitle: "Open on your phone", qrText: "Scan the QR code with your phone to open this page and add it to your home screen.",
        addBtn: "Add to home screen", dialogTitle: "Add to home screen",
        dialogDesc: "Add Costa Key as an app icon on your phone. The icon opens your personal page and chat directly.",
        installNow: "Install the app now", iosTitle: "iPhone / iPad (Safari)", androidTitle: "Android (Chrome)",
        ios: ["Tap the share icon at the bottom", "Choose 'Add to Home Screen'", "Tap 'Add' — done!"],
        android: ["Tap the menu (three dots) at the top right", "Choose 'Install' / 'Add shortcut'", "Confirm 'Install' — the icon appears on your home screen"] },
  de: { qrTitle: "Auf dem Handy öffnen", qrText: "Scannen Sie den QR-Code mit Ihrem Handy, um diese Seite zu öffnen und zum Startbildschirm hinzuzufügen.",
        addBtn: "Zum Startbildschirm hinzufügen", dialogTitle: "Zum Startbildschirm hinzufügen",
        dialogDesc: "Fügen Sie Costa Key als App-Symbol auf Ihrem Handy hinzu. Das Symbol öffnet direkt Ihre persönliche Seite und den Chat.",
        installNow: "App jetzt installieren", iosTitle: "iPhone / iPad (Safari)", androidTitle: "Android (Chrome)",
        ios: ["Tippen Sie unten auf das Teilen-Symbol", "Wählen Sie 'Zum Home-Bildschirm'", "Tippen Sie auf 'Hinzufügen' — fertig!"],
        android: ["Tippen Sie oben rechts auf das Menü (drei Punkte)", "Wählen Sie 'Installieren / Verknüpfung hinzufügen'", "Bestätigen Sie 'Installieren' — das Symbol erscheint auf dem Startbildschirm"] },
  fr: { qrTitle: "Ouvrir sur votre téléphone", qrText: "Scannez le QR code avec votre téléphone pour ouvrir cette page et l'ajouter à votre écran d'accueil.",
        addBtn: "Ajouter à l'écran d'accueil", dialogTitle: "Ajouter à l'écran d'accueil",
        dialogDesc: "Ajoutez Costa Key comme icône d'application sur votre téléphone. L'icône ouvre directement votre page personnelle et le chat.",
        installNow: "Installer l'application maintenant", iosTitle: "iPhone / iPad (Safari)", androidTitle: "Android (Chrome)",
        ios: ["Touchez l'icône de partage en bas", "Choisissez 'Sur l'écran d'accueil'", "Touchez 'Ajouter' — c'est fait !"],
        android: ["Touchez le menu (trois points) en haut à droite", "Choisissez 'Installer / Ajouter un raccourci'", "Confirmez 'Installer' — l'icône apparaît sur votre écran d'accueil"] },
  es: { qrTitle: "Abre en tu teléfono", qrText: "Escanea el código QR con tu teléfono para abrir esta página y añadirla a tu pantalla de inicio.",
        addBtn: "Añadir a la pantalla de inicio", dialogTitle: "Añadir a la pantalla de inicio",
        dialogDesc: "Añade Costa Key como icono de app en tu teléfono. El icono abre directamente tu página personal y el chat.",
        installNow: "Instalar la app ahora", iosTitle: "iPhone / iPad (Safari)", androidTitle: "Android (Chrome)",
        ios: ["Toca el icono de compartir abajo", "Elige 'Añadir a pantalla de inicio'", "Toca 'Añadir' — ¡listo!"],
        android: ["Toca el menú (tres puntos) arriba a la derecha", "Elige 'Instalar / Crear acceso directo'", "Confirma 'Instalar' — el icono aparece en tu pantalla de inicio"] },
};

export function InstallPWA({ lang = "nl" }) {
  const t = IT[lang] || IT.en;
  const [open, setOpen] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [qr, setQr] = useState("");
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isMobile = detectMobile();

  // Inject a per-guest (dynamic) manifest + Apple meta so the home-screen icon
  // opens straight back to THIS guest session/chat.
  useEffect(() => {
    if (isStandalone()) return;
    const link = document.querySelector('link[rel="manifest"]') || document.head.appendChild(Object.assign(document.createElement("link"), { rel: "manifest" }));
    const prevHref = link.getAttribute("href");
    link.setAttribute("href", `/api/public/manifest?start=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    const metas = [
      ["apple-mobile-web-app-capable", "yes"],
      ["mobile-web-app-capable", "yes"],
      ["apple-mobile-web-app-status-bar-style", "black-translucent"],
      ["apple-mobile-web-app-title", "Costa Key"],
    ].map(([name, content]) => {
      const m = document.createElement("meta");
      m.name = name;
      m.content = content;
      document.head.appendChild(m);
      return m;
    });
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/guest-sw.js", { scope: "/" }).catch(() => {});
    }
    return () => {
      if (prevHref) link.setAttribute("href", prevHref); else link.remove();
      metas.forEach((m) => m.remove());
    };
  }, []);

  useEffect(() => {
    const onBIP = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setOpen(false); };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Desktop: generate a QR code of the current guest URL so the guest can open it on their phone.
  useEffect(() => {
    if (isMobile || isStandalone()) return;
    QRCode.toDataURL(window.location.href, { width: 320, margin: 1, color: { dark: "#1c3d34", light: "#ffffff" } })
      .then(setQr)
      .catch(() => setQr(""));
  }, [isMobile]);

  if (isStandalone() || installed) return null;

  const nativeInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    setOpen(false);
  };

  const Step = ({ n, icon: Icon, children }) => (
    <li className="flex items-start gap-3">
      <span className="grid place-items-center h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">{n}</span>
      <span className="text-sm flex items-center gap-1.5 flex-wrap">{children}{Icon && <Icon className="h-4 w-4 inline text-muted-foreground" />}</span>
    </li>
  );

  // Desktop → QR code instead of the button
  if (!isMobile) {
    return (
      <div className="mt-5 inline-flex items-center gap-4 rounded-2xl bg-white/12 backdrop-blur px-4 py-3 ring-1 ring-white/25" data-testid="pwa-qr-block">
        {qr ? (
          <img src={qr} alt="QR-code naar gastenpagina" className="h-24 w-24 rounded-lg bg-white p-1.5 shrink-0" data-testid="pwa-qr-image" />
        ) : (
          <span className="grid place-items-center h-24 w-24 rounded-lg bg-white/20"><QrCode className="h-8 w-8 text-white/80" /></span>
        )}
        <div className="text-white max-w-[220px]">
          <p className="font-display font-semibold text-sm flex items-center gap-1.5"><Smartphone className="h-4 w-4" /> {t.qrTitle}</p>
          <p className="text-white/85 text-xs mt-1">{t.qrText}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="add-to-homescreen-btn"
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/30 transition-colors"
      >
        <Smartphone className="h-4 w-4" /> {t.addBtn}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="add-to-homescreen-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Smartphone className="h-5 w-5 text-primary" /> {t.dialogTitle}</DialogTitle>
            <DialogDescription>{t.dialogDesc}</DialogDescription>
          </DialogHeader>

          {deferred && (
            <Button onClick={nativeInstall} className="w-full bg-primary hover:bg-primary/90 gap-2" data-testid="pwa-install-now-btn">
              <Download className="h-4 w-4" /> {t.installNow}
            </Button>
          )}

          <div className="space-y-4 mt-1">
            <div className={`rounded-xl border p-4 ${isIOS ? "border-primary/50 bg-primary/5" : "border-border"}`} data-testid="ios-instructions">
              <p className="font-semibold text-sm flex items-center gap-1.5 mb-2"><Apple className="h-4 w-4" /> {t.iosTitle}</p>
              <ol className="space-y-2">
                <Step n="1" icon={Share}>{t.ios[0]}</Step>
                <Step n="2" icon={Plus}>{t.ios[1]}</Step>
                <Step n="3">{t.ios[2]}</Step>
              </ol>
            </div>

            <div className={`rounded-xl border p-4 ${isAndroid ? "border-primary/50 bg-primary/5" : "border-border"}`} data-testid="android-instructions">
              <p className="font-semibold text-sm flex items-center gap-1.5 mb-2"><Smartphone className="h-4 w-4" /> {t.androidTitle}</p>
              <ol className="space-y-2">
                <Step n="1" icon={MoreVertical}>{t.android[0]}</Step>
                <Step n="2" icon={Download}>{t.android[1]}</Step>
                <Step n="3">{t.android[2]}</Step>
              </ol>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
